import { NgModule } from "@angular/core";
import { Router, RouterModule, Routes } from "@angular/router";
import { EdpCallbackComponent } from "@edp-components/callback/callback.component";
import { EdpDeepLinkComponent } from "@edp-components/edp-deep-link/edp-deep-link.component";
import { RegionEnum } from "@edp-enums/region.enum";
import { UnavailabilityType } from "@edp-enums/unavailability.enum";
import { EdpAuthGuard } from "@edp-guards/auth.guard";
import { EdpAuthPlaneta } from "@edp-guards/auth.planeta";
import { EdpFuncionaGuard } from "@edp-guards/funciona.guard";
import { EdpHealthGuard } from "@edp-guards/health.guard";
import { EdpUnavailabilityGuard } from "@edp-guards/unavailability.guard";
import { RegionService } from "@edp-services/region.service";
import { CustomSerializer } from "@edp-utils/route-serializer";
import { StoreRouterConnectingModule } from "@ngrx/router-store";
import { LayoutComponent } from "./@layouts/layout/layout.component";
import { PublicLayoutComponent } from "./@layouts/public-layout/public-layout.component";
import { DatalayerResolver } from "./@resolvers/datalayer.resolver";

const routes: Routes = [
    {
        path: "planeta-zero/bem-vindo",
        loadChildren: () => import("@edp-pages/edpe/welcome/welcome.module").then(m => m.WelcomeModule),
        canActivate: [EdpAuthGuard],
    },
    {
        path: "planeta-zero/inscricao",
        loadChildren: () => import("@edp-pages/edpe/subscription/subscription.module").then(m => m.SubscriptionModule),
        canActivate: [EdpAuthGuard],
    },
    {
        path: "smart-now",
        loadChildren: () => import("@edp-pages/edpe/smart-now/smart-now.module").then(m => m.SmartNowModule),
        canActivate: [EdpAuthGuard],
        data: { canRedirect: true },
    },
    {
        path: "resumo-faturas/:contract_id",
        loadChildren: () => import("@edp-pages/billing-summary/billing-summary.module").then(m => m.BillingSummaryModule),
        canActivate: [EdpAuthGuard],
    },
    {
        path: "locais-consumo",
        loadChildren: () =>
            import("@edp-pages/contract-consumption-local/contract-consumption-local.module").then(m => m.ContractConsumptionLocalModule),
        canActivate: [EdpAuthGuard],
        data: { canRedirect: true },
        resolve: { datalayer: DatalayerResolver },
    },
    {
        path: "",
        component: LayoutComponent,
        canActivate: [EdpAuthGuard],
        canActivateChild: [EdpUnavailabilityGuard],
        children: [
            {
                path: "planeta-zero",
                loadChildren: () => import("@edp-pages/edpe/edpe.module").then(m => m.EdpeModule),
                canActivate: [EdpAuthPlaneta],
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.edpe",
                    operationHistory: "PLANET_PLANET_ZERO_VISITED_PAGE",
                    canRedirect: true,
                },
            },
            {
                path: "",
                redirectTo: "energia/inicio",
                pathMatch: "full",
            },
            {
                path: "energia/:contract_id/inicio",
                loadChildren: () => import("@edp-pages/dashboard/dashboard.module").then(m => m.DashboardModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                    unavailabilityToCheck: UnavailabilityType.SENV,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/inicio",
                loadChildren: () => import("@edp-pages/dashboard/dashboard.module").then(m => m.DashboardModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                    unavailabilityToCheck: UnavailabilityType.SENV,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/consumos",
                loadChildren: () => import("@edp-pages/consumptions/consumptions.module").then(m => m.ConsumptionsModule),
                data: {
                    noHeaderPadding: true,
                    mobileBreadcrumb: "pt.edp.edponline.consumptions.title",
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/funciona",
                loadChildren: () => import("@edp-pages/service-funciona/service-funciona.module").then(m => m.ServiceFuncionaModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.funciona",
                },
                canActivate: [EdpFuncionaGuard],
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/saude",
                loadChildren: () => import("@edp-pages/health/health.module").then(m => m.HealthModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.saude",
                },
                canActivate: [EdpHealthGuard],
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "novo-plano",
                loadChildren: () => import("@edp-pages/packs/packs.module").then(m => m.PacksModule),
                data: {
                    title: "edp.menu.contratarEnergia",
                    breadcrumb: "edp.menu.contratarEnergia",
                    translate: true,
                    hideMenuAndHeaderContract: true,
                    canRedirect: true,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/leituras",
                loadChildren: () => import("@edp-pages/readings/readings.module").then(m => m.ReadingsModule),
                data: {
                    title: "edp.menu.leituras",
                    breadcrumb: "edp.menu.leituras",
                    translate: true,
                    sideBar: true,
                    mobileBreadcrumb: "pt.edp.edponline.leituras.title",
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/eficiencia-detalhes",
                loadChildren: () =>
                    import(
                        "@edp-pages/consumptions/consumptions-smartnow-energy-efficiency/consumptions-smartnow-energy-efficiency-chart-detail-page/consumptions-smartnow-energy-efficiency-chart-detail-page.module"
                    ).then(m => m.ConsumptionsSmartnowEnergyEfficiencyChartDetailPageModule),
                data: {
                    translate: true,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "pedidos-apoio",
                loadChildren: () =>
                    import("@edp-pages/requests-and-operations/requests-and-operations.module").then(m => m.RequestsAndOperationsModule),
                data: {
                    title: "edp.menu.apoioCliente",
                    breadcrumb: "edp.menu.apoioCliente",
                    translate: true,
                    sideBar: true,
                    mobileBreadcrumb: "pt.edp.edponline.requests-and-operations.title",
                    canRedirect: true,
                    unavailabilityToCheck: UnavailabilityType.CRM,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/pagamentos-faturas",
                loadChildren: () => import("@edp-pages/billing/billing.module").then(m => m.BillingModule),
                data: {
                    title: "edp.menu.apoioCliente",
                    breadcrumb: "edp.menu.apoioCliente",
                    translate: true,
                    sideBar: true,
                    mobileBreadcrumb: "pt.edp.edponline.billing.title",
                    unavailabilityToCheck: UnavailabilityType.SENV,
                },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "energia/:contract_id/omie",
                loadChildren: () => import("@edp-pages/consumptions-omie/consumptions-omie.module").then(m => m.ConsumptionsModule),
                data: { canRedirect: true },
            },
            {
                path: "energia",
                loadChildren: () => import("@edp-pages/iframe/iframe.module").then(m => m.IframeModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                },
            },
            {
                path: "apoio",
                loadChildren: () => import("@edp-pages/iframe/iframe.module").then(m => m.IframeModule),
            },
            {
                path: "beneficios-old",
                loadChildren: () => import("@edp-pages/benefits-v2/benefits-v2.module").then(m => m.BenefitsV2Module),
                data: { canRedirect: true },
                resolve: { datalayer: DatalayerResolver },
            },
            {
                path: "beneficios",
                loadChildren: () => import("@edp-pages/benefits-v3/benefits-v3.module").then(m => m.BenefitsV3Module),
            },
            {
                path: "notificacoes",
                children: [
                    {
                        path: "",
                        redirectTo: "lista",
                        pathMatch: "full",
                    },
                    {
                        path: "lista",
                        loadChildren: () => import("@edp-pages/notifications/notifications.module").then(m => m.NotificationsModule),
                    },
                    {
                        path: "opt-out",
                        loadChildren: () => import("@edp-pages/notifications/opt-out/opt-out.module").then(m => m.OptOutModule),
                    },
                ],
                data: { canRedirect: true },
            },
            {
                path: "perfil",
                loadChildren: () => import("@edp-pages/profile/profile.module").then(m => m.ProfileModule),
                data: { canRedirect: true },
            },
        ],
    },
    {
        path: "",
        component: PublicLayoutComponent,
        children: [
            {
                path: "ap",
                loadChildren: () => import("@edp-pages/nif-validation/nif-validation.module").then(m => m.NifValidationModule),
            },
            {
                path: "autorizar-terceiros/:id",
                loadChildren: () =>
                    import("@edp-pages/third-party-shielding/third-party-shielding.module").then(m => m.ThirdPartyShieldingModule),
            },
            {
                path: "area-aberta/acordo-pagamento",
                loadChildren: () => import("@edp-pages/payment-agreement/payment-agreement.module").then(m => m.PaymentAgreementModule),
                data: {
                    clearHeader: true,
                },
            },
            {
                path: "area-aberta/acordo-pagamento/sem-acordo",
                loadChildren: () => import("@edp-pages/payment-agreement/no-agreement/no-agreement.module").then(m => m.NoAgreementModule),
            },
            {
                path: "area-aberta/acordo-pagamento/error",
                loadChildren: () =>
                    import("@edp-pages/payment-agreement/payment-agreement-error/payment-agreement-error.module").then(
                        m => m.PaymentAgreementErrorModule
                    ),
                data: {
                    clearHeader: true,
                },
            },
            {
                path: "area-aberta/acordo-pagamento/delay",
                loadChildren: () =>
                    import("@edp-pages/payment-agreement/payment-agreement-delay/payment-agreement-delay.module").then(
                        m => m.PaymentAgreementDelayModule
                    ),
                data: {
                    clearHeader: true,
                },
            },
        ],
    },
    {
        path: "pedido-assistencia",
        loadChildren: () => import("@edp-pages/assistance-request/assistance-request.module").then(m => m.AssistanceRequestModule),
        canActivate: [EdpAuthGuard, EdpFuncionaGuard],
        data: { canRedirect: true },
    },
    {
        path: "pack-simulator",
        loadChildren: () => import("@edp-pages/pack-simulator/pack-simulator.module").then(m => m.PackSimulatorModule),
        canActivate: [EdpAuthGuard],
        data: { canRedirect: true },
    },
    {
        path: "biometria",
        loadChildren: () =>
            import("@edp-pages/profile-app-recognition/profile-app-recognition.module").then(m => m.ProfileAppRecognitionModule),
        canActivate: [EdpAuthGuard],
    },
    {
        path: "callback",
        component: EdpCallbackComponent,
    },
    {
        path: "area-aberta/pagamento",
        loadChildren: () => import("@edp-pages/open-area-payments/open-area-payments.module").then(m => m.OpenAreaPaymentsModule),
    },
    {
        path: "editar-perfil",
        loadChildren: () => import("@edp-pages/profile-iframe/profile-iframe.module").then(m => m.ProfileIframeModule),
        data: { canRedirect: true },
    },
    {
        path: "**",
        component: EdpDeepLinkComponent,
        canActivate: [EdpAuthGuard],
    },
];

const routesES: Routes = [
    {
        path: "locais-consumo",
        loadChildren: () =>
            import("@edp-pages/ES/contract-local-details-es/contract-local-details-es.module").then(m => m.ContractLocalDetailsESModule),
        canActivate: [EdpAuthGuard],
        data: { canRedirect: true },
    },
    {
        path: "locais-consumo/:contract_id/inicio",
        loadChildren: () =>
            import("@edp-pages/ES/contract-local-details-es/contract-local-details-es.module").then(m => m.ContractLocalDetailsESModule),
        canActivate: [EdpAuthGuard],
        data: { canRedirect: true },
    },
    {
        path: "",
        component: LayoutComponent,
        canActivate: [EdpAuthGuard],
        canActivateChild: [EdpUnavailabilityGuard],
        children: [
            {
                path: "",
                redirectTo: "energia/inicio",
                pathMatch: "full",
            },
            {
                path: "energia/:contract_id/inicio",
                loadChildren: () => import("@edp-pages/ES/dashboard-es/dashboard-es.module").then(m => m.DashboardESModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                    unavailabilityToCheck: UnavailabilityType.SENV,
                },
            },
            {
                path: "energia/inicio",
                loadChildren: () => import("@edp-pages/ES/dashboard-es/dashboard-es.module").then(m => m.DashboardESModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                    unavailabilityToCheck: UnavailabilityType.SENV,
                },
            },
            {
                path: "process-detail",
                loadChildren: () =>
                    import("@edp-pages/ES/process-details-es/process-details-es.module").then(m => m.ProcessDetailsESModule),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                },
            },
            {
                path: "pedidos-apoio",
                loadChildren: () =>
                    import("@edp-pages/ES/request-and-operations-es/request-operations-es.module").then(
                        m => m.RequestsAndOperationsESModule
                    ),
                data: {
                    mobileBreadcrumb: "pt.edp.edponline.mobile.breadcrumb.energy",
                },
            },
            {
                path: "pedidos-apoio/detalhe/:id",
                loadChildren: () =>
                    import(
                        "@edp-pages/ES/request-and-operations-es/requests-operations-details-es/requests-operations-details-es.component"
                    ).then(m => m.RequestsOperationsDetailsESComponent),
            },
            {
                path: "perfil",
                loadChildren: () => import("@edp-pages/profile/profile.module").then(m => m.ProfileModule),
                data: { canRedirect: true },
            },
        ],
    },
    {
        path: "callback",
        component: EdpCallbackComponent,
    },
    {
        path: "editar-perfil",
        loadChildren: () => import("@edp-pages/profile-iframe/profile-iframe.module").then(m => m.ProfileIframeModule),
        data: { canRedirect: true },
    },
    {
        path: "**",
        component: EdpDeepLinkComponent,
        canActivate: [EdpAuthGuard],
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            enableTracing: false,
            scrollPositionRestoration: 'enabled',
            anchorScrolling: 'enabled'
        }),
        StoreRouterConnectingModule.forRoot({
            serializer: CustomSerializer,

        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {
    constructor(public readonly router: Router, private readonly regionService: RegionService) {
        if (this.regionService.getRegion() === RegionEnum.ES) {
            this.router.resetConfig(routesES);
        }
    }
}
